import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import './Admin.css';

interface Warranty {
  id: number;
  user_id: number;
  order_id: string;
  serial_number: string;
  product_image: string;
  product_cost: number;
  warranty_duration: number;
  payment_amount: number;
  start_date: string;
  end_date: string;
  status: string;

  user_mobile: string;
  refund_status: string;
  refund_qr_code: string;
  refund_amount: number;
  created_at: string;
}

interface WarrantyDetails {
  warranty: Warranty;
}

const AdminWarranties: React.FC = () => {
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selectedWarranty, setSelectedWarranty] = useState<WarrantyDetails | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchWarranties();
  }, [filter]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const orderId = searchParams.get('order_id');
    if (orderId) {
      setSearch(orderId);
    }
  }, []);

  const fetchWarranties = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get('/api/admin/warranties', { params });
      setWarranties(response.data.warranties);
    } catch (error) {
      console.error('Failed to fetch warranties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarrantyDetails = async (orderId: string) => {
    try {
      const response = await axios.get(`/api/admin/warranty/${orderId}`);
      setSelectedWarranty(response.data);
    } catch (error) {
      console.error('Failed to fetch warranty details:', error);
      alert('Failed to load warranty details');
    }
  };

  const filteredWarranties = warranties.filter(w =>
    (w.order_id || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleApprove = async (id: number) => {
    if (!window.confirm('Are you sure you want to approve this warranty? This will activate it and award credits.')) return;
    try {
      await axios.post(`/api/admin/warranties/${id}/approve`);
      fetchWarranties();
      alert('Warranty approved successfully');
    } catch (error) {
      console.error('Failed to approve warranty:', error);
      alert('Failed to approve warranty');
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm('Are you sure you want to reject this warranty?')) return;
    try {
      await axios.post(`/api/admin/warranties/${id}/reject`);
      fetchWarranties();
      alert('Warranty rejected successfully');
    } catch (error) {
      console.error('Failed to reject warranty:', error);
      alert('Failed to reject warranty');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Loading...</div>
      </>
    );
  }

  if (selectedWarranty) {
    const w = selectedWarranty.warranty;
    return (
      <>
        <Navbar />
        <div className="container">
          <h1 className="page-title">📦 Warranty Details</h1>
          <div className="card">
            <button
              onClick={() => setSelectedWarranty(null)}
              className="btn btn-secondary"
              style={{ marginBottom: '20px' }}
            >
              ← Back to List
            </button>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h3>Order Information</h3>
                <p><strong>Order ID:</strong> {w.order_id || 'N/A'}</p>
                <p><strong>Status:</strong> <span className={`status-badge status-${w.status}`}>{w.status}</span></p>
                <p><strong>User Mobile:</strong> {w.user_mobile}</p>
                <p><strong>Created At:</strong> {new Date(w.created_at.replace(' ', 'T') + 'Z').toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
              </div>
              <div>
                <h3>Payment Details</h3>
                <p><strong>Product Cost:</strong> ₹{w.product_cost?.toFixed(2) || '0.00'}</p>
                <p><strong>Payment Amount (70%):</strong> ₹{w.payment_amount?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <h3>Warranty Duration</h3>
                <p><strong>Duration:</strong> {w.warranty_duration || 3} {w.warranty_duration === 1 ? 'Month' : 'Months'}</p>
                <p><strong>Start Date:</strong> {new Date(w.start_date).toLocaleDateString()}</p>
                <p><strong>End Date:</strong> {new Date(w.end_date).toLocaleDateString()}</p>
              </div>
              <div>
                <h3>Product Image</h3>
                {w.product_image && (
                  <img
                    src={w.product_image}
                    alt="Product"
                    style={{ maxWidth: '200px', height: 'auto', borderRadius: '8px' }}
                  />
                )}
              </div>
              {w.status === 'pending' && (
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to approve this warranty? This will activate it and award credits.')) {
                        axios.post(`/api/admin/warranties/${w.id}/approve`)
                          .then(() => {
                            alert('Warranty approved successfully');
                            fetchWarranties();
                            setSelectedWarranty(null);
                          })
                          .catch(() => alert('Failed to approve warranty'));
                      }
                    }}
                    className="btn btn-primary"
                    style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                  >
                    Approve Warranty
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reject this warranty?')) {
                        axios.post(`/api/admin/warranties/${w.id}/reject`)
                          .then(() => {
                            alert('Warranty rejected successfully');
                            fetchWarranties();
                            setSelectedWarranty(null);
                          })
                          .catch(() => alert('Failed to reject warranty'));
                      }
                    }}
                    className="btn btn-danger"
                    style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                  >
                    Reject Warranty
                  </button>
                </div>
              )}
              {w.refund_status && w.refund_status !== 'none' && (
                <div style={{ gridColumn: 'span 2', marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                  <h3>Refund Request</h3>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <p><strong>Status:</strong> <span className={`status-badge status-${w.refund_status === 'approved' || w.refund_status === 'done' ? 'active' : w.refund_status}`}>{w.refund_status}</span></p>
                      <p><strong>Refund Amount:</strong> ₹{w.refund_amount?.toFixed(2) || '0.00'}</p>
                      {w.refund_status === 'pending' && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              if (window.confirm('Approve Refund?')) {
                                axios.post(`/api/admin/warranties/${w.id}/refund-status`, { status: 'approved' })
                                  .then(() => {
                                    alert('Refund Approved');
                                    fetchWarranties();
                                    setSelectedWarranty(null);
                                  })
                                  .catch(err => alert('Failed to approve refund'));
                              }
                            }}
                            style={{ backgroundColor: '#28a745' }}
                          >
                            Approve Refund
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              if (window.confirm('Reject Refund?')) {
                                axios.post(`/api/admin/warranties/${w.id}/refund-status`, { status: 'rejected' })
                                  .then(() => {
                                    alert('Refund Rejected');
                                    fetchWarranties();
                                    setSelectedWarranty(null);
                                  })
                                  .catch(err => alert('Failed to reject refund'));
                              }
                            }}
                            style={{ backgroundColor: '#dc3545', color: 'white' }}
                          >
                            Reject Refund
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p><strong>QR Code:</strong></p>
                      {w.refund_qr_code ? (
                        <img src={w.refund_qr_code} alt="Refund QR" style={{ maxWidth: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
                      ) : (
                        <p>No QR Code</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container">
        <h1 className="page-title">📦 Warranty Management</h1>
        <div className="filters" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
            <button
              className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('active')}
            >
              Active
            </button>
            <button
              className={`btn ${filter === 'expired' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('expired')}
            >
              Expired
            </button>

            <button
              className={`btn ${filter === 'refund_requests' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter('refund_requests')}
            >
              Refunds
            </button>
          </div>
          <div style={{ flex: 1, maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search Order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-control"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>User Mobile</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWarranties.map((warranty) => (
                <tr
                  key={warranty.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => warranty.order_id && fetchWarrantyDetails(warranty.order_id)}
                >
                  <td style={{ color: '#007bff', textDecoration: 'underline' }}>
                    {warranty.order_id || 'N/A'}
                  </td>
                  <td>{warranty.user_mobile}</td>
                  <td>{new Date(warranty.start_date).toLocaleDateString()}</td>
                  <td>{new Date(warranty.end_date).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge status-${warranty.status}`}>
                      {warranty.status}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {warranty.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          onClick={() => handleApprove(warranty.id)}
                          style={{ padding: '5px 10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          ✔
                        </button>
                        <button
                          onClick={() => handleReject(warranty.id)}
                          style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          ✖
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div >
    </>
  );
};

export default AdminWarranties;

